/**
 * 48 コマ表の列ヘッダー行を貼り付けるための実測フック。
 *
 * 判定規則そのものは `models/komaTheadSticky` の純関数が持つ (その JSDoc が
 * 移植元と理由の SoT)。ここは DOM を測って純関数へ渡すだけ。
 *
 * 同ディレクトリの `useForecastChartHeight` と同じ作法で書く:
 * 2 カラム判定は `[data-dft-workspace]` への DOM walk + computed display、
 * 再測定は ResizeObserver + `window.resize` の 150ms debounce、
 * state 更新は `prev === next` ガードで再描画ループを断つ (issue #6446 で
 * 同じ画面が自己参照ループを踏んでいる)。
 *
 * 要素の参照は **callback ref + state** で持つ。`useRef` + 空 deps だと、
 * 対象が条件描画で後から現れた場合 (KomaCard は BG 失効案内中に早期 return する)
 * に effect が二度と走らず、測り直せないまま 0 が残る。
 *
 * `ResizeObserver` は**存在する時だけ**生成する。未実装の環境では初回計測と
 * `window.resize` にフォールバックする (`useForecastChartHeight` と同じ)。
 */
import { useCallback, useLayoutEffect, useState } from 'react'

import {
  hasKomaTableHorizontalOverflow,
  shouldStickKomaThead,
} from '~/pages/DemandForecastDaily/models/komaTheadSticky'

const RESIZE_DEBOUNCE_MS = 150

function findWorkspaceRoot(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el
  while (node !== null) {
    if (node.dataset.dftWorkspace !== undefined) return node
    node = node.parentElement
  }
  return null
}

/**
 * 測り直しの購読をまとめる。`ResizeObserver` があれば `targets` を観測し、
 * 無ければ `window.resize` だけで賄う。戻り値は解除関数。
 */
function subscribeToResize(
  targets: readonly Element[],
  measure: () => void,
): () => void {
  let observer: ResizeObserver | undefined
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure)
    for (const target of targets) observer.observe(target)
  }

  let timer: ReturnType<typeof setTimeout> | undefined
  const handleResize = (): void => {
    clearTimeout(timer)
    timer = setTimeout(measure, RESIZE_DEBOUNCE_MS)
  }
  window.addEventListener('resize', handleResize)

  return () => {
    observer?.disconnect()
    clearTimeout(timer)
    window.removeEventListener('resize', handleResize)
  }
}

/**
 * 上に重なる sticky 帯 (48 コマ表カードの見出しバー) の実測高。
 *
 * バーは保存状態テキスト・競合時の追加ボタン・flexWrap で高さが変わるので
 * 定数化できない。design 側も同じ理由で実測して CSS 変数へ入れているが、
 * app では CSS 変数を持ち込まず prop (数値 px) で渡す
 * (`frontend/apps/ebalance/CLAUDE.md`「design HTML の CSS 変数を app へ
 * コピーしない」)。
 */
export function useStickyBarHeight(): {
  ref: (el: HTMLDivElement | null) => void
  height: number
} {
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    if (el === null) {
      setHeight(0)
      return
    }
    const measure = (): void => {
      const next = Math.round(el.getBoundingClientRect().height)
      setHeight((prev) => (prev === next ? prev : next))
    }
    measure()
    return subscribeToResize([el], measure)
  }, [el])

  return { ref: setEl, height }
}

/**
 * 表 wrap と table を測り、列ヘッダー行を貼り付けてよいかを返す。
 *
 * 列構成 (参照列数・当日限定列の有無) を引数に取るのは、**列が増減しても
 * wrap の box は変わらず ResizeObserver では拾えない**ため。不透明な deps 配列
 * ではなく値そのものを受けて、再測定のきっかけを型で見えるようにする。
 */
export function useKomaTheadSticky({
  referenceColumnCount,
  showTodaySection,
}: {
  referenceColumnCount: number
  showTodaySection: boolean
}): {
  wrapRef: (el: HTMLDivElement | null) => void
  tableRef: (el: HTMLTableElement | null) => void
  isSticky: boolean
} {
  const [wrap, setWrap] = useState<HTMLDivElement | null>(null)
  const [table, setTable] = useState<HTMLTableElement | null>(null)
  // 計測前は「収まっていない」= 現状の挙動 (横スクロール可・非 sticky) から始める。
  const [isSticky, setIsSticky] = useState(false)

  useLayoutEffect(() => {
    if (wrap === null || table === null) return

    const measure = (): void => {
      const root = findWorkspaceRoot(wrap)
      const isTwoColumn =
        root !== null && window.getComputedStyle(root).display === 'grid'
      const next = shouldStickKomaThead({
        hasHorizontalOverflow: hasKomaTableHorizontalOverflow(
          table.scrollWidth,
          wrap.clientWidth,
        ),
        isTwoColumn,
      })
      setIsSticky((prev) => (prev === next ? prev : next))
    }

    measure()
    // wrap だけでなく table も観測する。参照列の増減では wrap の box が
    // 変わらないため (`MasterDataTable/stickyActionColumn` と同じ理由)。
    return subscribeToResize([wrap, table], measure)
  }, [wrap, table, referenceColumnCount, showTodaySection])

  return {
    wrapRef: useCallback((el: HTMLDivElement | null) => {
      setWrap(el)
    }, []),
    tableRef: useCallback((el: HTMLTableElement | null) => {
      setTable(el)
    }, []),
    isSticky,
  }
}
